import env from '@/configs/env';
import type { PlatformApiRequest } from '@ai-doc-automation/contracts/openapi-client';

export interface ApiErrorDetails {
  error?: string;
  reason?: string | null;
  pagesBalance?: number | null;
  requiredPages?: number | null;
  action?: string | null;
}

export class HttpClient {
  private token: string | null = null;
  private activeTenantId: string | null = null;

  setToken(token: string): void {
    this.token = token;
  }

  setActiveTenantId(tenantId: string | null | undefined): void {
    this.activeTenantId = tenantId ?? null;
  }

  clearToken(): void {
    this.token = null;
    this.activeTenantId = null;
  }

  async getBlob(path: string): Promise<Blob> {
    return this.request<Blob>(path, {}, true, true);
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete(path: string): Promise<void> {
    await this.request<void>(path, { method: 'DELETE' });
  }

  async upload<T>(path: string, formData: FormData, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: formData,
      signal,
    });
  }

  async send<TResponse, TBody = unknown>(request: PlatformApiRequest<TBody>): Promise<TResponse> {
    const body =
      request.body instanceof FormData
        ? request.body
        : request.body !== undefined
          ? JSON.stringify(request.body)
          : undefined;

    return this.request<TResponse>(request.path, {
      method: request.method,
      body,
    });
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    retryOnUnauth = true,
    binaryResponse = false,
  ): Promise<T> {
    const headers: Record<string, string> = {
      ...(options.body instanceof FormData
        ? {}
        : { 'Content-Type': 'application/json' }),
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      ...(this.activeTenantId ? { 'X-Active-Tenant-Id': this.activeTenantId } : {}),
      ...(options.headers as Record<string, string> | undefined),
    };

    const isAbsolutePath = /^https?:\/\//i.test(path);
    const hasAbsoluteApiBase = /^https?:\/\//i.test(env.apiUrl);
    const targetPath = isAbsolutePath
      ? path
      : hasAbsoluteApiBase
        ? `${env.apiUrl.replace(/\/$/, '')}${path}`
        : path;
    const response = await fetch(targetPath, {
      ...options,
      headers,
    });

    if (response.status === 401 && response.headers.get('X-Session-Expired') === 'true') {
      window.dispatchEvent(new Event('app-session-expired'));
      throw new ApiError('Your session has expired. Please sign in again.', 401);
    }

    if (response.status === 401 && retryOnUnauth) {
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession({ forceRefresh: true });
        const newToken = session.tokens?.idToken?.toString();
        if (newToken) {
          this.token = newToken;
          return this.request<T>(path, options, false, binaryResponse);
        }
      } catch {
        // Refresh failed — let the 401 propagate
      }
    }

    if (!response.ok) {
      if (response.status === 401) window.dispatchEvent(new Event('app-session-expired'));
      const error = await response
        .json()
        .catch(() => ({ error: `HTTP ${response.status}` }));
      const details = error as ApiErrorDetails;
      throw new ApiError(
        details.error || `HTTP ${response.status}`,
        response.status,
        details,
      );
    }

    // Handle empty responses (204 No Content or empty body) and non-JSON payloads.
    if (binaryResponse) return await response.blob() as T;
    const text = await response.text();
    if (!text) return undefined as T;

    const contentType = response.headers.get('content-type')?.toLowerCase() || '';
    if (contentType.includes('application/json')) {
      return JSON.parse(text) as T;
    }

    return text as T;
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details: ApiErrorDetails = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get reason() {
    return this.details.reason ?? null;
  }

  get pagesBalance() {
    return this.details.pagesBalance ?? null;
  }

  get requiredPages() {
    return this.details.requiredPages ?? null;
  }

  get action() {
    return this.details.action ?? null;
  }
}

export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

export const httpClient = new HttpClient();
